<template>
  <section class="supervision-page">
    <Navbar />
    <section class="hero fixed-nav responsive-pad">
      <div class="hero-head pt-4 pb-4">
        <p class="title">
          Aufsichten
        </p>
      </div>

      <div class="hero-body p-0">
        <b-tabs v-if="monthTabs.length > 0" v-model="selectedMonthIndex" class="tab-custom">
          <b-tab-item
            v-for="month in monthTabs"
            :key="month.key"
            :label="month.label">
          </b-tab-item>
        </b-tabs>

        <b-table class="app-table"
          :data="visibleSupervisions"
          :row-class="rowClass"
          :bordered="false"
          :striped="false"
          :narrowed="false"
          :hoverable="true"
          :loading="false"
          :focusable="false"
          :mobile-cards="true"
          :paginated="false">
          <b-table-column field="date" label="Datum" v-slot="props">
            <div class="date-display">
              {{ formatDate(props.row.entry.date) }}
            </div>
            <p v-if="isHoliday(props.row.entry.date)" class="holiday-label">
              {{ getHolidayName(props.row.entry.date) }}
            </p>
          </b-table-column>
          <b-table-column field="primary.name" label="Aufsicht" v-slot="props">
            <b-autocomplete append-to-body
              v-model="props.row.entry.primary.name"
              :placeholder="$t('teacher-management.search-placeholder')"
              :keep-first="true"
              :open-on-focus="true"
              :data="search_teachers"
              field="fullName"
              :empty="[]"
              :disabled="isHoliday(props.row.entry.date)"
              @input="search(props.row.entry.primary.name)"
              @select="selectTeacher($event, props.row.index, 'primary')">
              <template #empty>Keine Ergebnisse</template>
            </b-autocomplete>
          </b-table-column>
          <b-table-column field="secondary.name" label="Vertreter" v-slot="props">
            <b-autocomplete append-to-body
              v-model="props.row.entry.secondary.name"
              :placeholder="$t('teacher-management.search-placeholder')"
              :keep-first="true"
              :open-on-focus="true"
              :data="search_teachers"
              field="fullName"
              :empty="[]"
              :disabled="isHoliday(props.row.entry.date)"
              @input="search(props.row.entry.secondary.name)"
              @select="selectTeacher($event, props.row.index, 'secondary')">
              <template #empty>Keine Ergebnisse</template>
            </b-autocomplete>
          </b-table-column>
        </b-table>

        <div class="supervision-actions">
          <Button :type="'zoom'" @click="addWeek" :text="'Woche hinzufügen'" />
          <Button
            :disabled="!changed"
            :type="'zoom'"
            @click="save"
            :text="'Änderungen speichern'" />
        </div>
      </div>
    </section>
    <Footer />
  </section>
</template>

<script>
import moment from 'moment';
import Navbar from '../components/Common/Navbar.vue';
import Footer from '../components/Common/Footer.vue';
import Button from '../components/Common/Button.vue';

export default {
  components: {
    Navbar,
    Footer,
    Button,
  },

  data() {
    return {
      changed: false,
      selectedMonthIndex: 0,
      hasSelectedCurrentMonth: false,
    };
  },

  created() {
    this.$store.dispatch('Account/load');
    this.$store.dispatch('Teachers/load');
  },

  computed: {
    supervisions() {
      const entries = this.$store.getters['Account/supervisions'];

      if (!Array.isArray(entries)) {
        return [];
      }

      return entries;
    },
    datedSupervisions() {
      return this.supervisions.reduce((result, entry, index) => {
        const date = moment(entry.date, 'YYYY-MM-DD', true);

        if (!date.isValid()) {
          return result;
        }

        return [...result, { entry, index, date }];
      }, []).sort((left, right) => left.date.valueOf() - right.date.valueOf());
    },
    monthTabs() {
      const uniqueMonths = this.datedSupervisions.reduce((result, { date }) => {
        const key = date.format('YYYY-MM');
        if (result.some((month) => month.key === key)) {
          return result;
        }

        return [...result, {
          key,
          label: date.locale('de').format('MMMM YYYY'),
        }];
      }, []);

      return uniqueMonths.sort((left, right) => left.key.localeCompare(right.key));
    },
    selectedMonthKey() {
      if (this.monthTabs.length === 0) {
        return '';
      }

      const selectedMonth = this.monthTabs[this.selectedMonthIndex];
      if (selectedMonth) {
        return selectedMonth.key;
      }

      return this.monthTabs[0].key;
    },
    visibleSupervisions() {
      if (this.selectedMonthKey === '') {
        return this.datedSupervisions;
      }

      return this.datedSupervisions.filter(({ date }) => date.format('YYYY-MM') === this.selectedMonthKey);
    },
    teachers() {
      return this.$store.getters['Teachers/get_teacher_records'];
    },
    search_teachers() {
      if (this.$store.getters['Teachers/get_search_list'].length === 0) {
        return this.teachers;
      }
      return this.$store.getters['Teachers/get_search_list'];
    },
  },

  methods: {
    createEntry() {
      return {
        date: '',
        primary: {
          name: '',
          id: '',
        },
        secondary: {
          name: '',
          id: '',
        },
      };
    },
    normalizeEntry(entry = {}) {
      return {
        ...entry,
        date: entry.date || entry.day || '',
        primary: this.normalizeTeacher(entry.primary),
        secondary: this.normalizeTeacher(entry.secondary),
      };
    },
    normalizeTeacher(teacher = {}) {
      if (typeof teacher === 'string') {
        return {
          name: teacher,
          id: '',
        };
      }

      const firstname = teacher.firstname || '';
      const lastname = teacher.lastname || '';
      const fullName = [firstname, lastname].filter((part) => part !== '').join(' ').trim();

      return {
        ...teacher,
        name: teacher.name || teacher.fullName || fullName || teacher.label || '',
        id: teacher.id || teacher.teacherId || '',
      };
    },
    search(name) {
      if (name === '') {
        return;
      }
      this.$store.dispatch('Teachers/search_teacher', name);
    },
    change() {
      this.changed = true;
    },
    rowClass(row) {
      if (this.isHoliday(row.entry.date)) {
        return 'is-holiday';
      }
      return '';
    },
    selectTeacher(teacher, rowIndex, field) {
      this.$set(this.supervisions[rowIndex], field, {
        name: teacher.fullName,
        id: teacher.id,
      });
      this.changed = true;
    },
    getBaseDate() {
      if (this.supervisions.length === 0) {
        return moment().startOf('isoWeek');
      }

      const datedEntries = this.supervisions
        .map((entry) => moment(entry.date || entry.day, 'YYYY-MM-DD', true))
        .filter((date) => date.isValid());

      if (datedEntries.length === 0) {
        return moment().startOf('isoWeek');
      }

      return moment.max(datedEntries).add(1, 'week').startOf('isoWeek');
    },
    createWeekEntries() {
      const startOfWeek = this.getBaseDate();
      const entries = [];

      for (let dayOffset = 0; dayOffset < 5; dayOffset += 1) {
        const date = moment(startOfWeek).add(dayOffset, 'days').format('YYYY-MM-DD');
        entries.push({
          ...this.createEntry(),
          date,
        });
      }

      return entries;
    },
    addWeek() {
      const entries = this.createWeekEntries();
      entries.forEach((entry) => {
        this.supervisions.push(entry);
      });
      this.changed = true;
    },
    save() {
      this.$store.dispatch('Account/save_supervision').then((response) => {
        if (response) {
          this.$buefy.toast.open({
            message: 'Aufsichten erfolgreich gespeichert',
            type: 'is-success',
          });
          this.changed = false;
          this.$store.dispatch('Account/load');
        } else {
          this.$buefy.toast.open({
            message: 'Etwas ist schiefgelaufen...',
            type: 'is-danger',
          });
        }
      });
    },
    getHolidayName(date) {
      const currentDate = moment(date, 'YYYY-MM-DD', true);

      if (!currentDate.isValid()) {
        return '';
      }

      const holiday = this.getHolidayMap(currentDate.year())[currentDate.format('YYYY-MM-DD')];
      return holiday || '';
    },
    formatDate(date) {
      const currentDate = moment(date, 'YYYY-MM-DD', true);

      if (!currentDate.isValid()) {
        return date;
      }

      return currentDate.format('DD.MM.YYYY');
    },
    isHoliday(date) {
      return this.getHolidayName(date) !== '';
    },
    getHolidayMap(year) {
      const easterSunday = this.getEasterSunday(year);
      const holidays = [
        { date: moment(`${year}-01-01`), name: 'Neujahr' },
        { date: moment(easterSunday).subtract(2, 'days'), name: 'Karfreitag' },
        { date: moment(easterSunday).add(1, 'days'), name: 'Ostermontag' },
        { date: moment(`${year}-05-01`), name: 'Tag der Arbeit' },
        { date: moment(easterSunday).add(39, 'days'), name: 'Christi Himmelfahrt' },
        { date: moment(easterSunday).add(50, 'days'), name: 'Pfingstmontag' },
        { date: moment(easterSunday).add(60, 'days'), name: 'Fronleichnam' },
        { date: moment(`${year}-10-03`), name: 'Tag der Deutschen Einheit' },
        { date: moment(`${year}-11-01`), name: 'Allerheiligen' },
        { date: moment(`${year}-12-25`), name: '1. Weihnachtstag' },
        { date: moment(`${year}-12-26`), name: '2. Weihnachtstag' },
      ];

      return holidays.reduce((result, holiday) => ({
        ...result,
        [holiday.date.format('YYYY-MM-DD')]: holiday.name,
      }), {});
    },
    getEasterSunday(year) {
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = ((19 * a) + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
      const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
      const month = Math.floor((h + l - (7 * m) + 114) / 31);
      const day = ((h + l - (7 * m) + 114) % 31) + 1;

      return moment({
        year,
        month: month - 1,
        day,
      });
    },
  },

  watch: {
    supervisions: {
      handler(entries) {
        for (let index = 0; index < entries.length; index += 1) {
          const normalized = this.normalizeEntry(entries[index]);
          const needsNormalization = JSON.stringify({
            date: entries[index].date || entries[index].day || '',
            primary: entries[index].primary,
            secondary: entries[index].secondary,
          }) !== JSON.stringify({
            date: normalized.date,
            primary: normalized.primary,
            secondary: normalized.secondary,
          });

          if (needsNormalization) {
            this.$set(entries, index, normalized);
          }
        }
      },
      immediate: true,
    },
    monthTabs: {
      handler(months) {
        if (!this.hasSelectedCurrentMonth) {
          const currentMonthIndex = months.findIndex((month) => month.key === moment().format('YYYY-MM'));
          if (currentMonthIndex !== -1) {
            this.selectedMonthIndex = currentMonthIndex;
            this.hasSelectedCurrentMonth = true;
            return;
          }
        }

        if (months.length === 0) {
          this.selectedMonthIndex = 0;
          return;
        }

        if (this.selectedMonthIndex >= months.length) {
          this.selectedMonthIndex = months.length - 1;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
.supervision-page {
  min-height: 100vh;

  .date-display {
    min-height: 2.5em;
    display: flex;
    align-items: center;
  }

  .holiday-label {
    margin-top: 0.35rem;
    font-size: 0.85rem;
    color: #6b7280;
  }

  .supervision-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding-bottom: 1rem;
    flex-wrap: wrap;
  }

  .table tr.is-holiday {
    background-color: #f3f4f6 !important;
    color: #6b7280;
    margin-top: -1rem;
  }

  .table tr.is-holiday:hover {
    background-color: #f3f4f6 !important;
  }
}
</style>
